import { rarityMeta, hexToRgba, initials, ACCENT_NEON } from '@/utils/characters';

/**
 * The signature element of Hakken City: a character card that "breathes" with a
 * slow city-pulse glow shifting between the character's brand colour and the
 * app accent. See `.city-pulse` in globals.css. Theme-aware (light + dark).
 */
export default function CharacterCard({ character, onClick }) {
  const brand = character.brandColor || ACCENT_NEON;
  const rarity = rarityMeta(character.rarity);

  return (
    <button
      type="button"
      onClick={() => onClick?.(character)}
      className="city-pulse group relative w-full overflow-hidden rounded-2xl border-2 bg-surface p-4 text-left transition-transform duration-200 active:scale-[0.98]"
      style={{
        '--pulse-a': brand,
        '--pulse-b': ACCENT_NEON,
        background: `linear-gradient(150deg, ${hexToRgba(brand, 0.18)} 0%, rgb(var(--hc-surface)) 60%, rgb(var(--hc-surface)) 100%)`,
      }}
    >
      {/* rarity tag */}
      <span
        className="absolute right-3 top-3 rounded-full px-2 py-0.5 font-micro text-[10px] font-bold uppercase tracking-wider"
        style={{ color: rarity.color, background: hexToRgba(rarity.color, 0.14), border: `1px solid ${hexToRgba(rarity.color, 0.4)}` }}
      >
        {rarity.label}
      </span>

      {/* met / relationship indicator */}
      {character.relationship?.met && (
        <span
          className="absolute bottom-3 right-3 rounded-full px-2 py-0.5 font-micro text-[10px] font-bold"
          style={{ color: brand, background: hexToRgba(brand, 0.16), border: `1px solid ${hexToRgba(brand, 0.45)}` }}
        >
          ✦ Nível {character.relationship.stage ?? 0}
        </span>
      )}

      <div className="flex items-center gap-3">
        {/* avatar */}
        <div
          className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-2xl font-urban text-2xl font-extrabold text-white"
          style={{
            background: hexToRgba(brand, 0.22),
            border: `2px solid ${hexToRgba(brand, 0.55)}`,
            boxShadow: `inset 0 0 18px ${hexToRgba(brand, 0.35)}`,
          }}
        >
          {character.avatarUrl ? (
            <img src={character.avatarUrl} alt={character.name} className="h-full w-full object-cover" />
          ) : (
            initials(character.name)
          )}
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="font-urban text-lg font-extrabold leading-tight text-fg">{character.name}</h3>
          <p className="truncate text-xs text-fg/70">{character.occupation}</p>
          <div className="mt-1 flex items-center gap-1.5 font-micro text-[11px] text-muted">
            <span style={{ color: brand }}>◉</span>
            <span className="truncate">{character.neighborhood}</span>
            {character.age ? <span className="opacity-70">· {character.age}</span> : null}
          </div>
        </div>
      </div>

      {character.bio && (
        <p className="mt-3 line-clamp-2 text-[13px] leading-relaxed text-fg/65">
          {character.bio.split('\n')[0]}
        </p>
      )}
    </button>
  );
}
